/**
 * What a printed item card says: the page that goes in the binder or on the
 * shelf beside the item, where the small label only names it.
 *
 * The card is a paper record kept with the item, but it is still not the
 * item's whole record. It carries the **public** fields, the serial number
 * (which is stamped on the thing already, the same exception the small label
 * makes) and the stock facts that are not private. It never carries notes,
 * private fields, unit cost or the vendor's invoice number: a sheet in a
 * binder is one photocopy away from a stranger. See docs/notes/invariants.md.
 */

import {
  SERIAL_FIELD_LABEL,
  STOCK_STATE_LABELS,
  TRACKING_LABELS,
  type Movement,
  type StockDetail,
} from "./stock.js";
import { CONDITION_LABELS, STATUS_LABELS, publicFields, type Item } from "./types.js";

/** More than this and the page stops having room for handwriting. */
export const CARD_MAX_SPECS = 12;

/**
 * The movement log's printed rows. The latest this many of the item's history
 * are printed; the rest of the table stays ruled and blank for whoever moves
 * it next, with never fewer than `CARD_BLANK_LOG_ROWS` of those.
 */
export const CARD_MAX_LOG = 8;
export const CARD_LOG_ROWS = 6;
export const CARD_BLANK_LOG_ROWS = 2;

export interface CardFact {
  label: string;
  value: string;
}

/** One printed line of the movement log. */
export interface CardLogLine {
  /** `YYYY-MM-DD`, the day it happened. */
  date: string;
  /** "Deployed to Acme Corp", "Returned from Acme Corp", "Received". */
  what: string;
  quantity: number;
  /** The part of the actor's email before the @: a column to be read at a glance, not an address. */
  by: string;
}

export interface ItemCard {
  title: string;
  /** From the field labelled "Serial number", whatever its visibility; empty when there is none. */
  serial: string;
  facts: CardFact[];
  description: string;
  /** Public fields other than the serial, in the order the item has them. */
  specs: CardFact[];
  /** Public fields beyond `CARD_MAX_SPECS` that did not fit. */
  moreSpecs: number;
  tags: string[];
  /** The item's stock history, oldest first, as much of it as fits. Empty outside a stockroom. */
  log: CardLogLine[];
  /** Earlier history lines that did not fit. */
  moreLog: number;
}

export interface ItemCardInput {
  item: Item;
  /** Names, already resolved: the card does not know about the taxonomy. */
  categoryName: string;
  where: string;
  /** Present in a stockroom, for an item that is on the books. */
  stock?: { detail: StockDetail; clientName: string } | null;
}

/**
 * `2026-09-23` from an epoch in **seconds**, which is what every timestamp the
 * worker writes is. Reading one as milliseconds prints a day in January 1970.
 */
const day = (seconds: number): string => new Date(seconds * 1000).toISOString().slice(0, 10);

/** What a history line says on paper. The movement's note stays off it, like the item's notes. */
function logWhat(movement: Movement): string {
  const client = movement.clientName;
  switch (movement.kind) {
    case "received":
      return "Received";
    case "deployed":
      return client ? `Deployed to ${client}` : "Deployed";
    case "returned":
      return client ? `Returned from ${client}` : "Returned";
    case "retired":
      return "Retired";
    case "reserved":
      return client ? `Held for ${client}` : "Held in reserve";
    case "released":
      return client ? `Released from ${client}'s reserve` : "Released from reserve";
    case "converted":
      return "Converted";
  }
}

export function cardLog(movements: Movement[]): { log: CardLogLine[]; moreLog: number } {
  const lines = movements.map((movement) => ({
    date: movement.occurredOn,
    what: logWhat(movement),
    quantity: movement.quantity,
    by: movement.actor.split("@")[0] ?? "",
  }));
  return {
    log: lines.slice(-CARD_MAX_LOG),
    moreLog: Math.max(0, lines.length - CARD_MAX_LOG),
  };
}

export function serialOfItem(item: Item): string {
  return (
    item.fields
      .find((field) => field.label.trim().toLowerCase() === SERIAL_FIELD_LABEL.toLowerCase())
      ?.value.trim() ?? ""
  );
}

export function buildItemCard({ item, categoryName, where, stock }: ItemCardInput): ItemCard {
  const facts: CardFact[] = [];
  const add = (label: string, value: string) => {
    if (value.trim()) facts.push({ label, value: value.trim() });
  };

  add("Category", categoryName);
  add("Location", where);
  if (item.condition) add("Condition", CONDITION_LABELS[item.condition]);
  if (item.quantity > 1) add("Quantity", String(item.quantity));

  if (stock) {
    const { stock: record, receipt } = stock.detail;
    add("Tracking", TRACKING_LABELS[record.tracking]);
    const spot =
      stock.detail.deployments.find(
        (line) => line.clientId === record.clientId && line.quantity > line.returned,
      )?.spot ?? "";
    add(
      "Stock",
      record.state === "deployed" && stock.clientName
        ? `${STOCK_STATE_LABELS[record.state]}, at ${stock.clientName}${spot ? `, ${spot}` : ""}`
        : STOCK_STATE_LABELS[record.state],
    );
    if (record.tracking === "bulk") add("On hand", String(record.onHand));
    add("Received", [receipt?.receivedOn, receipt?.vendor].filter(Boolean).join(", "));
  } else {
    add("Status", STATUS_LABELS[item.status]);
  }
  add("Added", day(item.createdAt));

  const serial = serialOfItem(item);
  const specs = publicFields(item.fields)
    .filter((field) => field.label.trim().toLowerCase() !== SERIAL_FIELD_LABEL.toLowerCase())
    .filter((field) => field.label.trim() && field.value.trim())
    .map((field) => ({ label: field.label.trim(), value: field.value.trim() }));

  return {
    title: item.title.trim() || "Untitled",
    serial,
    facts,
    description: item.description.trim(),
    specs: specs.slice(0, CARD_MAX_SPECS),
    moreSpecs: Math.max(0, specs.length - CARD_MAX_SPECS),
    tags: item.tags,
    ...(stock ? cardLog(stock.detail.movements) : { log: [], moreLog: 0 }),
  };
}
